/**
 * [BL] Add-on tray — the "Hold it, add with this product" mode of the [BL] Add-on Categories block
 * (blocks/addon-categories.liquid). Like Hydrogen: add-ons chosen in quick add aren't added to the
 * cart straight away; they collect in a list under the categories (with the current product first),
 * and the product's own Add to cart sends everything in one request.
 *
 * - Quick add (assets/quick-add.js) calls `addItem()` instead of adding to the cart when the tile's
 *   tray is in hold mode; the item carries its cart properties (personalisation) captured from the
 *   modal's form.
 * - The product form (assets/product-form.js) fires `bl:cart-add:before` with its FormData; the tray
 *   rewrites it to Shopify's multi-item format (`items[i][id]`, `items[i][quantity]`,
 *   `items[i][properties][key]`) with the product first, then clears itself on `bl:cart-add:success`.
 * - State (see _internal/docs/personalisation-state.md): the URL is the record of WHAT is held, in
 *   Hydrogen's format — `additionalProductIds={"N":"gid://shopify/ProductVariant/ID"}` plus the
 *   personaliser's `.N` params for a personalised add-on. sessionStorage (per product) caches the
 *   complete items (cart properties, display details) so a refresh is instant, and restores the set
 *   after leaving and coming back in the same tab. A URL that names add-ons the cache doesn't know (a
 *   shared link, an ad link) is rebuilt from the block's catalog; personalised ones get their cart
 *   properties from the personaliser's `BLPersonaliser.cartProperties` (nothing is rendered).
 * - Clicking a row re-opens quick add for that item to change it (`BLQuickAdd.edit`), under the same
 *   index; closing without saving puts its URL params back (`restoreItem`).
 * - An add-on keeps its index N for as long as it is held.
 *
 * Mode comes from the block setting (`data-mode`); `?bl_addon_mode=hold|cart` overrides it for QA.
 */

import { formatMoneyLike } from '@theme/bl-money';

const STORAGE_PREFIX = 'bl-addon-tray:';
const MODE_OVERRIDE_PARAM = 'bl_addon_mode';

/**
 * @typedef {Object} TrayItem
 * @property {string} key - Unique id of this held item
 * @property {string} variantId
 * @property {number} quantity
 * @property {Record<string, string>} properties - Cart line properties (personalisation)
 * @property {string} title
 * @property {string} [subtitle] - Variant / personalisation summary
 * @property {string} [image]
 * @property {string} [price] - Display price (already formatted)
 * @property {string} [compareAt] - Display compare-at price (already formatted)
 * @property {number} [priceCents] - Price in cents (for the set total)
 * @property {number} [compareAtCents] - Compare-at price in cents (for the set total)
 * @property {number} [paramIndex] - This add-on's index N in the URL (additionalProductIds / `.N` params)
 * @property {Record<string, string>} [params] - Its `.N` URL params when held (to restore the URL later)
 * @property {string} [productUrl] - The add-on's product URL (for editing)
 * @property {number} [productId]
 * @property {boolean} [pending] - Rebuilt from a URL; cart properties still being worked out
 */

const ADDITIONAL_IDS = 'additionalProductIds';
/**
 * `choose.N=Letter`: add-on N was added by a link (sections/bl-occasions) without the shopper choosing
 * that option yet; its variant is a placeholder. The row asks for the choice and the set can't be
 * added to the cart until it's made (editing the item and saving clears it).
 */
const CHOOSE_KEY = 'choose';
/** The page product's Add to cart buttons: the form's own and the sticky bar's. */
const ATC_BUTTONS =
  '.product-information add-to-cart-component .add-to-cart-button, sticky-add-to-cart .add-to-cart-button';
/** Params that carry a design (as opposed to a variant option like `Material.N`). */
const DESIGN_KEY = /^(input|file|fp)\./;

/**
 * Personalisation types and the cart properties that hold what the shopper entered (see the app's
 * cart/customAttributes.ts). A held item of one of these types with none of its properties set hasn't
 * been personalised yet. Types not listed are never flagged.
 * @type {Record<string, string[]>}
 */
const PERSONALISATION_FIELDS = {
  initial: ['initial', 'message'],
  'letter-clip': ['initial', 'message'],
  'fluted-clip': ['initial', 'message'],
  'fluted-pendant': ['initial', 'message'],
  'fluted-pendant-v2': ['initial', 'message'],
  'moon-phase': ['date'],
  'moon-phase-clip': ['date'],
  zodiac: ['zodiac'],
  'zodiac-clip': ['zodiac'],
  'hand-drawing': ['handwriting', 'file'],
  'handwriting-clip': ['handwriting', 'file'],
  fingerprint: ['fingerprints', 'Fingerprint'],
  'fingerprint-clip': ['fingerprints', 'Fingerprint'],
  'bar-engraving': ['front', 'back'],
  'custom-card-holder': ['message'],
  'rectangular-tablet': ['initial'],
  'initial-tag': ['initial'],
};

/** @param {Record<string, string>} properties */
function visibleProperties(properties) {
  return Object.entries(properties).filter(([key, value]) => !key.startsWith('_') && value);
}

/** @param {string} key */
function humanise(key) {
  const words = key.replace(/[-_]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

class AddonTray extends HTMLElement {
  /** @type {TrayItem[]} */
  #items = [];
  /** Keys of the held items in the product add that is in flight (cleared from the set on success). @type {string[] | null} */
  #sending = null;
  /** True while held items are still being rebuilt from a URL: the product can't be added yet. */
  #busy = false;

  connectedCallback() {
    this.list = /** @type {HTMLElement} */ (this.querySelector('[data-bl-tray-list]'));
    try {
      this.product = JSON.parse(this.querySelector('[data-bl-tray-product]')?.textContent || '{}');
    } catch {
      this.product = { variants: {} };
    }
    try {
      this.catalog = JSON.parse(this.querySelector('[data-bl-addon-catalog]')?.textContent || '{}');
    } catch {
      this.catalog = { products: [] };
    }
    this.#items = this.mode === 'hold' ? this.#seed() : this.#load();
    // "Only the set from the link" with no set in the link (or the tab's cache): nothing to lock to, so
    // the shopper gets the full picker instead of a necklace with no way to add charms.
    if (this.hasAttribute('data-locked') && !this.#items.length) {
      this.removeAttribute('data-locked');
      this.closest('[data-locked]')?.removeAttribute('data-locked');
    }
    this.list?.addEventListener('click', this.#onRowClick);

    document.addEventListener('bl:cart-add:before', this.#onBeforeAdd);
    document.addEventListener('bl:cart-add:success', this.#onAddSuccess);
    // The product's selected variant changes the first row.
    document.addEventListener('change', this.#onPageChange, true);
    this.#render();

    // The theme re-renders the buy buttons on a variant change: put the total back.
    const productInfo = document.querySelector('.product-information');
    if (productInfo) {
      // The product area re-renders its buttons on a variant change; morph can replace them anywhere in
      // it, so the whole area is watched, but the callback does nothing unless a total or a busy state
      // has to be put back (nothing held, nothing to do).
      this.buttonObserver = new MutationObserver(() => {
        if (this.totalFrame || (!this.#items.length && !this.#busy)) return;
        this.totalFrame = requestAnimationFrame(() => {
          this.totalFrame = 0;
          const stale = Array.from(
            document.querySelectorAll(ATC_BUTTONS)
          ).some((button) => this.#items.length > 0 && this.mode === 'hold' && !button.querySelector('[data-bl-atc-set]'));
          if (stale) this.#render();
        });
      });
      this.buttonObserver.observe(productInfo, { childList: true, subtree: true });
      // The sticky bar is re-rendered on a variant change too.
      const sticky = document.querySelector('sticky-add-to-cart');
      if (sticky) this.buttonObserver.observe(sticky, { childList: true, subtree: true });
    }

    this.#hydratePending();
  }

  disconnectedCallback() {
    document.removeEventListener('bl:cart-add:before', this.#onBeforeAdd);
    document.removeEventListener('bl:cart-add:success', this.#onAddSuccess);
    document.removeEventListener('change', this.#onPageChange, true);
    this.buttonObserver?.disconnect();
  }

  /** 'hold' or 'cart' — the block setting, unless ?bl_addon_mode= overrides it. */
  get mode() {
    const override = new URLSearchParams(window.location.search).get(MODE_OVERRIDE_PARAM);
    if (override === 'hold' || override === 'cart') return override;
    return this.dataset.mode === 'hold' ? 'hold' : 'cart';
  }

  get holdLabel() {
    return this.dataset.holdLabel || 'Add to my set';
  }

  /** @returns {TrayItem[]} */
  get items() {
    return [...this.#items];
  }

  /**
   * The page product's selected variant and the held items with the set total, for anything that mirrors
   * the set (the keepsake LP's "Your keepsake set" panel). Pending items aren't in it or the total yet,
   * like the Add to cart total.
   */
  get summary() {
    const variant = this.product?.variants?.[this.#currentVariantId()] || {};
    const { now, was, template } = this.#totals();
    return {
      product: { title: this.product?.title || '', price: variant.price || '', compareAt: variant.compareAt || '' },
      items: this.#items.filter((item) => !item.pending),
      now,
      was,
      total: formatMoneyLike(template, now),
      wasTotal: formatMoneyLike(template, was),
      saved: was > now ? formatMoneyLike(template, was - now) : '',
    };
  }

  get editLabel() {
    return this.dataset.editLabel || 'Update my set';
  }

  /**
   * @param {Omit<TrayItem, 'key'>} item
   * @returns {string} The new item's key (pass it to `reveal()`).
   */
  addItem(item) {
    const key = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const held = this.#register({ ...item, key });
    this.#items.push(held);
    this.#commit();
    // For tracking (assets/bl-tracking.js): an item joined the set (not on edit, restore or URL seeding).
    this.dispatchEvent(new CustomEvent('bl-tray:add', { bubbles: true, detail: { item: { ...held } } }));
    return key;
  }

  /**
   * Swap a held item for an edited one, keeping its place in the list.
   * @param {string} key
   * @param {Omit<TrayItem, 'key'>} item
   */
  replaceItem(key, item) {
    const position = this.#items.findIndex((entry) => entry.key === key);
    if (position < 0) return this.addItem(item);
    const previous = this.#items[position];
    // The edit keeps the item's index (quick add re-opens it under the same one).
    const next = { ...item, key, paramIndex: item.paramIndex || previous.paramIndex };
    if (previous.paramIndex && previous.paramIndex !== next.paramIndex) this.#clearParamIndex(previous.paramIndex);
    // Saving the edit is the shopper's choice: the "choose" marker goes.
    const chosen = `${CHOOSE_KEY}.${next.paramIndex}`;
    this.#writeParams((params) => params.delete(chosen));
    this.#items[position] = this.#register(next);
    this.#commit();
    return key;
  }

  /** Give the item its URL index, write it to the URL and remember its params. @param {TrayItem} item */
  #register(item) {
    if (!item.paramIndex) item.paramIndex = this.#nextIndex();
    const index = item.paramIndex;
    this.#writeParams((params) => {
      const ids = this.#readIds(params);
      ids[String(index)] = `gid://shopify/ProductVariant/${item.variantId}`;
      params.set(ADDITIONAL_IDS, JSON.stringify(ids));
    });
    item.params = this.#paramsFor(index);
    const product = this.#catalogProduct(item.variantId);
    if (product) {
      item.productUrl = product.url;
      item.productId = product.id;
    }
    return item;
  }

  #commit() {
    this.#save();
    this.#render();
    this.dispatchEvent(new CustomEvent('bl-tray:change', { bubbles: true, detail: { items: this.items } }));
  }

  /** Scroll the set into view and highlight a row (the one just held). @param {string} [key] */
  reveal(key) {
    if (this.hidden) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const row = key ? this.querySelector(`[data-key="${key}"]`) : null;
    const target = /** @type {HTMLElement} */ (row || this);
    target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
    // Smooth scrolling can be cancelled (focus returning from the dialog, a busy main thread): if the
    // set still isn't on screen shortly after, jump to it.
    setTimeout(() => {
      const rect = target.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > window.innerHeight) target.scrollIntoView({ behavior: 'auto', block: 'center' });
    }, 900);
    if (row) {
      row.classList.remove('bl-tray__item--new');
      // Restart the animation if the class was already there.
      void (/** @type {HTMLElement} */ (row).offsetWidth);
      row.classList.add('bl-tray__item--new');
    }
  }

  /**
   * Put a held item's entry and `.N` params back in the URL exactly as they were when it was held
   * (quick add was opened to change it and closed without saving).
   * @param {string} key
   */
  restoreItem(key) {
    const item = this.#items.find((entry) => entry.key === key);
    if (!item?.paramIndex) return;
    this.#clearParamIndex(item.paramIndex);
    this.#restoreToUrl(item);
  }

  /** @param {string} key */
  removeItem(key) {
    const item = this.#items.find((entry) => entry.key === key);
    this.#items = this.#items.filter((entry) => entry.key !== key);
    if (item?.paramIndex) this.#clearParamIndex(item.paramIndex);
    this.#commit();
  }

  clear() {
    for (const item of this.#items) if (item.paramIndex) this.#clearParamIndex(item.paramIndex);
    this.#items = [];
    this.#commit();
  }

  // --- Storage ---------------------------------------------------------------------------------

  /** Per product and currency: held items carry formatted prices, so a market switch starts fresh. */
  get #storageKey() {
    const currency = this.dataset.currency ? `:${this.dataset.currency}` : '';
    return `${STORAGE_PREFIX}${this.dataset.productHandle || window.location.pathname}${currency}`;
  }

  /** @returns {TrayItem[]} */
  #load() {
    try {
      const raw = sessionStorage.getItem(this.#storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  #save() {
    try {
      if (this.#items.length) sessionStorage.setItem(this.#storageKey, JSON.stringify(this.#items));
      else sessionStorage.removeItem(this.#storageKey);
    } catch {
      // Storage blocked: the tray still works for this page view.
    }
  }

  // --- URL ------------------------------------------------------------------------------------
  // The personaliser's store owns the URL once it's running (it rewrites the whole query string from
  // its own copy), so every write goes through it when it exists, and straight to the URL otherwise.

  /** @returns {URLSearchParams} */
  #readParams() {
    const store = /** @type {any} */ (window).__blStore;
    return store?.toSearchParams ? store.toSearchParams() : new URLSearchParams(window.location.search);
  }

  /** @param {(params: URLSearchParams) => void} mutate */
  #writeParams(mutate) {
    const store = /** @type {any} */ (window).__blStore;
    const hasStore = Boolean(store?.toSearchParams && store?.replaceAll);
    const params = hasStore ? store.toSearchParams() : new URLSearchParams(window.location.search);
    const before = params.toString();
    mutate(params);
    if (params.toString() === before) return;
    if (hasStore) {
      store.replaceAll(params);
    } else {
      const qs = params.toString();
      window.history.replaceState(window.history.state, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
    }
  }

  /** @param {URLSearchParams} params @returns {Record<string, string>} */
  #readIds(params) {
    try {
      const parsed = JSON.parse(params.get(ADDITIONAL_IDS) || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  /** The `.N` params of one index. @param {number} index @returns {Record<string, string>} */
  #paramsFor(index) {
    const suffix = `.${index}`;
    /** @type {Record<string, string>} */
    const own = {};
    this.#readParams().forEach((value, key) => {
      if (key.endsWith(suffix)) own[key.slice(0, -suffix.length)] = value;
    });
    return own;
  }

  /** The lowest index above every one in use (URL ids, `.N` params, held items). */
  #nextIndex() {
    const params = this.#readParams();
    const used = [
      ...Object.keys(this.#readIds(params)).map(Number),
      ...Array.from(params.keys()).map((key) => Number(key.match(/\.(\d+)$/)?.[1])),
      ...this.#items.map((item) => item.paramIndex || 0),
    ].filter((n) => Number.isFinite(n));
    return Math.max(0, ...used) + 1;
  }

  /** Remove an add-on's entry and `.N` params from the URL. */
  #clearParamIndex(/** @type {number} */ index) {
    this.#writeParams((params) => {
      const suffix = `.${index}`;
      for (const key of Array.from(params.keys())) if (key.endsWith(suffix)) params.delete(key);
      const ids = this.#readIds(params);
      delete ids[String(index)];
      if (Object.keys(ids).length) params.set(ADDITIONAL_IDS, JSON.stringify(ids));
      else params.delete(ADDITIONAL_IDS);
    });
  }

  // --- Catalog ----------------------------------------------------------------------------------

  /** @param {string} variantId */
  #catalogProduct(variantId) {
    return (this.catalog?.products || []).find((/** @type {any} */ product) => product.variants?.[variantId]) || null;
  }

  /** A held item rebuilt from the catalog alone (no cart properties yet). */
  /** A variant's own image from the catalog, if it has one. @param {string} variantId */
  #variantImage(variantId) {
    return this.#catalogProduct(variantId)?.variants?.[variantId]?.[4] || '';
  }

  #itemFromCatalog(/** @type {string} */ variantId, /** @type {number} */ index) {
    const product = this.#catalogProduct(variantId);
    if (!product) return null;
    const [variantTitle, price, compareAt] = product.variants[variantId];
    const discount = Number(this.catalog?.discount) || 0;
    const priceCents = Math.round((price * (100 - discount)) / 100);
    const compareAtCents = compareAt > price ? compareAt : price;
    const template = Object.values(this.product?.variants || {})[0]?.price || '';
    const params = this.#paramsFor(index);
    const hasDesign = Object.keys(params).some((key) => DESIGN_KEY.test(key));
    return /** @type {TrayItem} */ ({
      key: `url-${index}`,
      variantId,
      quantity: 1,
      properties: {},
      title: product.title,
      subtitle: variantTitle && variantTitle !== 'Default Title' ? variantTitle : '',
      image: this.#variantImage(variantId) || product.image,
      price: formatMoneyLike(template, priceCents),
      compareAt: compareAtCents > priceCents ? formatMoneyLike(template, compareAtCents) : null,
      priceCents,
      compareAtCents,
      paramIndex: index,
      params,
      productUrl: product.url,
      productId: product.id,
      // A personalised add-on needs its cart properties from the personaliser before it can be added.
      pending: Boolean(product.personalisable && hasDesign),
    });
  }

  // --- Seeding ----------------------------------------------------------------------------------

  /**
   * What's held when the page loads. The URL decides; the session cache supplies complete items.
   * - URL names add-ons → keep cached items for those indexes, rebuild the rest from the catalog.
   * - URL names none → the tab's cached set comes back, and is written to the URL again.
   * @returns {TrayItem[]}
   */
  #seed() {
    const cached = this.#load();

    // An index quick add was still working on when the page was left (refresh with quick add open):
    // a new add-on was never added, so it comes out of the URL; a held one that was being changed
    // goes back to how it was held.
    try {
      const draft = Number(sessionStorage.getItem('bl-addon-draft'));
      sessionStorage.removeItem('bl-addon-draft');
      if (draft) {
        this.#clearParamIndex(draft);
        const held = cached.find((item) => item.paramIndex === draft);
        if (held) this.#restoreToUrl(held);
      }
    } catch {
      // Storage blocked: nothing recorded.
    }

    const ids = this.#readIds(new URLSearchParams(window.location.search));
    const indexes = Object.keys(ids);

    if (!indexes.length) {
      for (const item of cached) this.#restoreToUrl(item);
      return cached;
    }

    /** @type {TrayItem[]} */
    const items = [];
    for (const index of indexes) {
      const variantId = String(ids[index]).split('/').pop() || '';
      const known = cached.find((item) => String(item.paramIndex) === index && item.variantId === variantId && !item.pending);
      const item = known || this.#itemFromCatalog(variantId, Number(index));
      if (item) items.push(item);
    }
    return items;
  }

  /** Put a cached item's id and `.N` params back in the URL. @param {TrayItem} item */
  #restoreToUrl(item) {
    if (!item.paramIndex) return;
    const index = item.paramIndex;
    this.#writeParams((params) => {
      const ids = this.#readIds(params);
      ids[String(index)] = `gid://shopify/ProductVariant/${item.variantId}`;
      params.set(ADDITIONAL_IDS, JSON.stringify(ids));
      for (const [key, value] of Object.entries(item.params || {})) params.set(`${key}.${index}`, value);
    });
  }

  /**
   * Work out the cart properties of personalised add-ons that came from a URL. The personaliser
   * computes them straight from the URL's design (`BLPersonaliser.cartProperties`): no fetch, nothing
   * rendered, and the add-on keeps its index. A personaliser too old to have that function falls back
   * to mounting it off-screen (`BLQuickAdd.hydrate`), which moves the design to a new index.
   */
  async #hydratePending() {
    const pending = this.#items.filter((item) => item.pending);
    if (!pending.length) {
      this.#save();
      return;
    }
    this.#setBusy(true);
    const personaliser = await this.#personaliserApi(pending[0].productUrl);
    for (const item of pending) {
      try {
        const product = this.#catalogProduct(item.variantId);
        if (personaliser && product?.type && item.paramIndex) {
          const values = /** @type {string[]} */ (product.variants[item.variantId]?.[3] || []);
          item.properties = personaliser.cartProperties({
            customType: product.type,
            productHandle: this.dataset.productHandle,
            index: item.paramIndex,
            selectedOptions: values.map((value, i) => ({ name: product.optionNames?.[i] || '', value })),
            defaultMaterial: product.defaultMaterial || undefined,
          });
          if (!Object.keys(item.properties).length) throw new Error('Personaliser produced no cart properties');
        } else {
          const api = await this.#quickAddApi();
          const result = await api.hydrate({
            productUrl: item.productUrl,
            variantId: item.variantId,
            sourceIndex: item.paramIndex,
            baseProductHandle: this.dataset.productHandle,
          });
          const previousIndex = item.paramIndex;
          item.properties = result.properties;
          item.paramIndex = result.paramIndex;
          if (previousIndex && previousIndex !== result.paramIndex) this.#clearParamIndex(previousIndex);
        }
        item.pending = false;
        item.params = this.#paramsFor(/** @type {number} */ (item.paramIndex));
      } catch (error) {
        console.error('[bl-addon-tray] Could not rebuild a personalised add-on from the URL', error);
        // Without its engraving data it can't be made: drop it rather than add a blank charm.
        this.#items = this.#items.filter((entry) => entry !== item);
        if (item.paramIndex) this.#clearParamIndex(item.paramIndex);
      }
    }
    this.#setBusy(false);
    this.#commit();
  }

  /**
   * The personaliser's API, if this page loads it and it can compute cart properties by itself.
   * @returns {Promise<{ cartProperties(options: object): Record<string, string> } | null>}
   */
  async #personaliserApi(/** @type {string | undefined} */ productUrl) {
    const current = () => /** @type {any} */ (window).BLPersonaliser;
    if (!current() && document.querySelector('script[src*="personaliser.js"]')) {
      // The page product is personalisable: its own script is on its way.
      for (let attempt = 0; attempt < 50 && !current(); attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    } else if (!current()) {
      // A page product without personalisation (the Keepsake Necklace) doesn't load the script: take
      // its address from the add-on's quick-add content, once per tab.
      for (const fresh of [false, true]) {
        try {
          const src = await this.#personaliserScriptUrl(productUrl, fresh);
          if (!src) break;
          await import(src);
          break;
        } catch (error) {
          if (fresh) console.error('[bl-addon-tray] Could not load the personaliser', error);
        }
      }
    }
    return current()?.cartProperties ? current() : null;
  }

  /** Where personaliser.js lives (the app serves it from a versioned address). */
  async #personaliserScriptUrl(/** @type {string | undefined} */ productUrl, /** @type {boolean} */ fresh) {
    const cacheKey = 'bl-personaliser-src';
    try {
      const cached = fresh ? null : sessionStorage.getItem(cacheKey);
      if (cached) return cached;
    } catch {
      // Storage blocked: look it up.
    }
    if (!productUrl) return null;
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('view', 'quick-add');
    const response = await fetch(url.pathname + url.search);
    if (!response.ok) return null;
    const src = (await response.text()).match(/<script[^>]+src="([^"]*\/personaliser\.js[^"]*)"/)?.[1]?.replace(/&amp;/g, '&');
    if (!src) return null;
    try {
      sessionStorage.setItem(cacheKey, src);
    } catch {
      // Storage blocked: looked up again next time.
    }
    return src.startsWith('//') ? `https:${src}` : src;
  }

  /** assets/quick-add.js exposes hydrate / edit once it has loaded. */
  async #quickAddApi() {
    for (let attempt = 0; attempt < 50; attempt += 1) {
      const api = /** @type {any} */ (window).BLQuickAdd;
      if (api?.hydrate) return api;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    throw new Error('Quick add is not available');
  }

  /** While items are still being rebuilt, the product can't be added (it would miss them). */
  #setBusy(/** @type {boolean} */ busy) {
    this.#busy = busy;
    this.toggleAttribute('data-busy', busy);
    this.#applyBusy();
  }

  /** While busy the product's buttons are disabled (re-applied when the theme re-renders them). */
  #applyBusy() {
    for (const button of /** @type {NodeListOf<HTMLButtonElement>} */ (document.querySelectorAll(ATC_BUTTONS))) {
      if (button.closest('#quick-add-modal-content, .quick-add-modal')) continue;
      if (this.#busy) {
        if (!button.hasAttribute('data-bl-busy-disabled') && !button.disabled) {
          button.setAttribute('data-bl-busy-disabled', '');
          button.disabled = true;
        }
        button.setAttribute('aria-busy', 'true');
      } else {
        if (button.hasAttribute('data-bl-busy-disabled')) {
          button.removeAttribute('data-bl-busy-disabled');
          button.disabled = false;
        }
        button.removeAttribute('aria-busy');
      }
    }
  }

  // --- Editing ----------------------------------------------------------------------------------

  /** @param {Event} event */
  #onRowClick = async (event) => {
    const target = /** @type {Element} */ (event.target);
    if (!(target instanceof Element) || target.closest('.bl-tray__remove')) return;
    const row = /** @type {HTMLElement | null} */ (target.closest('.bl-tray__item[data-key]'));
    const item = row ? this.#items.find((entry) => entry.key === row.dataset.key) : null;
    if (!item || item.pending || !item.productUrl) return;
    try {
      const api = await this.#quickAddApi();
      row?.setAttribute('aria-busy', 'true');
      await api.edit(this, item);
    } catch (error) {
      console.error(error);
    } finally {
      row?.removeAttribute('aria-busy');
    }
  };

  // --- The product's add to cart -----------------------------------------------------------------

  /** The page product's own form: not quick add, not recommendations. @param {HTMLFormElement} form */
  #isPageProductForm(form) {
    return Boolean(
      form &&
        !form.closest('#quick-add-modal-content, .quick-add-modal, product-card, .product-recommendations') &&
        form.closest('.product-information, featured-product-information, product-information')
    );
  }

  /** @param {Event} event */
  #onBeforeAdd = (event) => {
    const { form, formData } = /** @type {CustomEvent} */ (event).detail || {};
    if (this.mode !== 'hold' || !this.#items.length || !this.#isPageProductForm(form)) return;
    // Held items still being rebuilt would be left out (and then lost): don't add until they're ready.
    if (this.#items.some((item) => item.pending)) {
      event.preventDefault();
      return;
    }
    // An add-on still waiting for its letter / month: bring its row up instead of adding a placeholder.
    const unchosen = this.#items.find((item) => item.params?.[CHOOSE_KEY]);
    if (unchosen) {
      event.preventDefault();
      this.reveal(unchosen.key);
      return;
    }
    const ready = this.#items;

    // The product (with its own personalisation), then every held add-on.
    const product = {
      id: String(formData.get('id') || ''),
      quantity: String(formData.get('quantity') || '1'),
      sellingPlan: formData.get('selling_plan'),
      properties: /** @type {[string, string][]} */ ([]),
    };
    for (const [key, value] of Array.from(formData.entries())) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && typeof value === 'string') product.properties.push([match[1], value]);
    }
    if (!product.id) return;

    for (const key of Array.from(formData.keys())) {
      if (key === 'id' || key === 'quantity' || key === 'selling_plan' || key.startsWith('properties[')) {
        formData.delete(key);
      }
    }

    formData.append('items[0][id]', product.id);
    formData.append('items[0][quantity]', product.quantity);
    if (product.sellingPlan) formData.append('items[0][selling_plan]', String(product.sellingPlan));
    for (const [key, value] of product.properties) formData.append(`items[0][properties][${key}]`, value);

    ready.forEach((item, i) => {
      const n = i + 1;
      formData.append(`items[${n}][id]`, item.variantId);
      formData.append(`items[${n}][quantity]`, String(item.quantity || 1));
      for (const [key, value] of Object.entries(item.properties || {})) {
        formData.append(`items[${n}][properties][${key}]`, value);
      }
    });

    this.#sending = ready.map((item) => item.key);
  };

  /** @param {Event} event */
  #onAddSuccess = (event) => {
    if (!this.#sending) return;
    const { form } = /** @type {CustomEvent} */ (event).detail || {};
    if (!this.#isPageProductForm(form)) return;
    const sent = new Set(this.#sending);
    this.#sending = null;
    // "Only the set from the link" (data-locked): the set stays as it is, so the page (whose hero and button
    // show this set) still matches it and another tap adds the same set again.
    if (this.hasAttribute('data-locked')) {
      this.#commit();
      return;
    }
    // Only what went into the cart leaves the set.
    for (const item of this.#items) if (sent.has(item.key) && item.paramIndex) this.#clearParamIndex(item.paramIndex);
    this.#items = this.#items.filter((item) => !sent.has(item.key));
    this.#commit();
  };

  /** @param {Event} event */
  #onPageChange = (event) => {
    const target = /** @type {Element} */ (event.target);
    if (target instanceof Element && target.closest('.product-information, variant-picker')) {
      requestAnimationFrame(() => this.#render());
    }
  };

  // --- Rendering ---------------------------------------------------------------------------------

  #announcing = false;

  /** After this render, tell set mirrors what the set is now (`bl-tray:render`, once per frame of changes). */
  #announce() {
    if (this.#announcing) return;
    this.#announcing = true;
    queueMicrotask(() => {
      this.#announcing = false;
      this.dispatchEvent(new CustomEvent('bl-tray:render', { bubbles: true, detail: this.summary }));
    });
  }

  /**
   * The page product's selected variant. The variant picker's JSON is always in the page; the product
   * form isn't when the variant can't be added (sold out / waitlist renders a sign-up form instead), and
   * the URL's `variant=` covers a picker that hasn't rendered.
   */
  #currentVariantId() {
    try {
      const picker = document.querySelector('.product-information variant-picker script[type="application/json"]');
      const id = picker?.textContent ? String(JSON.parse(picker.textContent).id || '') : '';
      if (id && this.product?.variants?.[id]) return id;
    } catch {
      // Fall through.
    }
    const input = /** @type {HTMLInputElement | null} */ (
      document.querySelector('.product-information form[action*="/cart/add"] input[name="id"]')
    );
    if (input?.value) return input.value;
    const fromUrl = new URLSearchParams(window.location.search).get('variant') || '';
    if (fromUrl && this.product?.variants?.[fromUrl]) return fromUrl;
    return Object.keys(this.product?.variants || {})[0] || '';
  }

  #render() {
    this.#announce();
    const show = this.mode === 'hold' && this.#items.length > 0;
    this.hidden = !show;
    this.toggleAttribute('data-has-items', this.#items.length > 0);
    if (!this.list) return;
    if (!show) {
      this.list.replaceChildren();
      this.#renderTotal();
      return;
    }

    const variant = this.product?.variants?.[this.#currentVariantId()] || {};
    const rows = [
      this.#row({
        title: this.product?.title || '',
        subtitle: variant.title && variant.title !== 'Default Title' ? variant.title : '',
        image: variant.image || this.product?.image,
        price: variant.price,
        compareAt: variant.compareAt,
        current: true,
      }),
      ...this.#items.map((item) =>
        this.#row({
          key: item.key,
          pending: item.pending,
          choose: item.params?.[CHOOSE_KEY],
          pickOption: this.#pickOption(item),
          needsPersonalisation: this.#needsPersonalisation(item),
          editable: Boolean(item.productUrl) && !item.pending,
          title: item.title,
          subtitle: [
            // A placeholder variant ("18k Gold Plated / Z") would read as chosen: leave it out.
            item.params?.[CHOOSE_KEY] ? '' : item.subtitle,
            ...visibleProperties(item.properties || {}).map(([key, value]) =>
              /^(https?:)?\/\//.test(value) ? `${humanise(key)}: added` : `${humanise(key)}: ${value}`
            ),
          ]
            .filter(Boolean)
            .join(' · '),
          // The variant's own photo (the letter, the month's stone), not the first photo quick add showed.
          image: this.#variantImage(item.variantId) || item.image,
          price: item.price,
          compareAt: item.compareAt,
        })
      ),
    ];
    this.list.replaceChildren(...rows);
    this.#renderHeader();
    this.#renderTotal();
  }

  /** A personalisable add-on that was held without anything entered. @param {TrayItem} item */
  #needsPersonalisation(item) {
    if (item.pending) return false;
    const type = this.#catalogProduct(item.variantId)?.type;
    const fields = type ? PERSONALISATION_FIELDS[type] : null;
    if (!fields) return false;
    return !fields.some((field) => (item.properties || {})[field]);
  }

  /**
   * "Only the set from the link" (data-locked): the option a held charm can still be changed on (the
   * month, the letter), named on its row as an invitation to tap it. Material follows the necklace, so
   * it isn't offered. @param {TrayItem} item
   */
  #pickOption(item) {
    if (!this.hasAttribute('data-locked') || !this.dataset.pickLabel || item.pending) return '';
    const names = this.#catalogProduct(item.variantId)?.optionNames || [];
    return names.filter((/** @type {string} */ name) => !/material/i.test(name)).pop() || '';
  }

  /** "$54 AUD" → "$54": rows drop the currency code. @param {string} money */
  #shortMoney(money) {
    return money.replace(/\s*[A-Z]{3}\s*$/, '').trim();
  }

  /** Header: how many pieces, and what the set saves. */
  #renderHeader() {
    const count = /** @type {HTMLElement | null} */ (this.querySelector('[data-bl-tray-count]'));
    const saving = /** @type {HTMLElement | null} */ (this.querySelector('[data-bl-tray-saving]'));
    if (count) count.textContent = String(this.#items.length + 1);
    if (!saving) return;
    const { now, was, template } = this.#totals();
    const saved = was - now;
    saving.hidden = !(saved > 0 && template);
    if (!saving.hidden) saving.textContent = `${this.dataset.savingLabel || 'You save'} ${formatMoneyLike(template, saved)}`;
  }

  // --- Total on the product's Add to cart ----------------------------------------------------------

  /** Product + held items: what the shopper pays, and the undiscounted total. */
  #totals() {
    const variant = this.product?.variants?.[this.#currentVariantId()] || {};
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      document.querySelector('.product-information form[action*="/cart/add"] input[name="quantity"]')
    );
    const productQuantity = Number(quantityInput?.value) || 1;
    let now = (Number(variant.priceCents) || 0) * productQuantity;
    let was = (Number(variant.compareAtCents) || Number(variant.priceCents) || 0) * productQuantity;
    for (const item of this.#items) {
      if (item.pending) continue; // not addable yet
      const quantity = item.quantity || 1;
      const price = Number(item.priceCents) || 0;
      now += price * quantity;
      was += (Number(item.compareAtCents) || price) * quantity;
    }
    return { now, was, template: variant.price || '' };
  }

  /** Whether the product's button carries the price even with nothing held (remembered across re-renders). */
  #buttonShowsPrice = false;

  #renderTotal() {
    if (this.#busy) this.#applyBusy();
    if (document.querySelector('.product-information add-to-cart-component [data-bl-atc-price]')) this.#buttonShowsPrice = true;
    const stickyShowsPrice = Boolean(document.querySelector('sticky-add-to-cart [data-bl-atc-price]'));

    const show = this.mode === 'hold' && this.#items.length > 0;
    const buttons = document.querySelectorAll(ATC_BUTTONS);
    for (const button of buttons) {
      if (button.closest('#quick-add-modal-content, .quick-add-modal')) continue;
      let total = button.querySelector('[data-bl-atc-total]');
      // An unavailable selection ("Sold out"): no price or set total on a button you can't press.
      // (Disabled only while busy is still a live button: it keeps its total.)
      const unavailable =
        button.hasAttribute('data-bl-unavailable') || (button.disabled && !button.hasAttribute('data-bl-busy-disabled'));
      if (unavailable) {
        total?.remove();
        button.classList.remove('bl-atc--has-total');
        continue;
      }
      // A button that shows the product's price by itself (theme setting) keeps it when nothing is
      // held; otherwise the total only appears with held items.
      if (!show && !total?.hasAttribute('data-bl-atc-price')) {
        total?.remove();
        button.classList.remove('bl-atc--has-total');
        continue;
      }
      const { now, was, template } = this.#totals();
      if (!template) continue;
      if (!total) {
        total = document.createElement('span');
        total.className = 'bl-atc-total';
        total.setAttribute('data-bl-atc-total', '');
        const sticky = Boolean(button.closest('sticky-add-to-cart'));
        if (sticky ? stickyShowsPrice : this.#buttonShowsPrice) total.setAttribute('data-bl-atc-price', '');
        button.prepend(total);
      }
      const current = document.createElement('span');
      current.className = 'bl-atc-total__now';
      current.textContent = formatMoneyLike(template, now);
      const parts = [current];
      if (was > now) {
        const original = document.createElement('s');
        original.textContent = formatMoneyLike(template, was);
        parts.push(original);
      }
      total.replaceChildren(...parts);
      // Marks a total this script wrote: a re-rendered button comes back with the server's plain
      // price (no marker) and gets the set total again.
      total.toggleAttribute('data-bl-atc-set', true);
      button.classList.add('bl-atc--has-total');
    }
  }

  /**
   * @param {{ key?: string, title: string, subtitle?: string, image?: string, price?: string,
   *   compareAt?: string | null, current?: boolean, editable?: boolean, pending?: boolean,
   *   needsPersonalisation?: boolean, choose?: string, pickOption?: string }} row
   */
  #row({ key, title, subtitle, image, price, compareAt, current, editable, pending, needsPersonalisation, choose, pickOption }) {
    const li = document.createElement('li');
    li.className = 'bl-tray__item';
    li.dataset.blTrayItem = current ? 'product' : 'addon';
    if (key) li.dataset.key = key;
    if (pending) li.setAttribute('aria-busy', 'true');
    if (editable) {
      // The whole row opens the item to change it (the name is the keyboard target).
      li.classList.add('bl-tray__item--editable');
    }

    const media = document.createElement('span');
    media.className = 'bl-tray__media';
    if (image) {
      const img = document.createElement('img');
      img.src = image;
      img.alt = '';
      img.width = 48;
      img.height = 48;
      img.loading = 'lazy';
      media.append(img);
    }

    const text = document.createElement('span');
    text.className = 'bl-tray__text';
    const name = document.createElement(editable ? 'button' : 'span');
    name.className = 'bl-tray__name';
    name.textContent = title;
    if (editable) {
      /** @type {HTMLButtonElement} */ (name).type = 'button';
      name.setAttribute('aria-label', `${this.dataset.editHint || 'Edit'} ${title}`);
    }
    text.append(name);
    if (subtitle) {
      const sub = document.createElement('span');
      sub.className = 'bl-tray__sub';
      sub.textContent = subtitle;
      text.append(sub);
    }

    if (choose) {
      // Same treatment as a missing personalisation: the row opens the item to choose.
      li.dataset.needsPersonalisation = '';
      const missing = document.createElement('span');
      missing.className = 'bl-tray__missing';
      missing.textContent = (this.dataset.chooseLabel || 'Choose your {option}').replace(
        '{option}',
        choose.toLowerCase()
      );
      text.append(missing);
    } else if (needsPersonalisation && this.dataset.missingLabel) {
      // The row is the way in: tapping it re-opens the item to personalise it.
      li.dataset.needsPersonalisation = '';
      const missing = document.createElement('span');
      missing.className = 'bl-tray__missing';
      missing.textContent = this.dataset.missingLabel;
      text.append(missing);
    } else if (pickOption && editable) {
      // Locked set: nothing is missing, but the row can be opened to pick another month or letter.
      const pick = document.createElement('span');
      pick.className = 'bl-tray__pick';
      pick.innerHTML =
        '<svg viewBox="0 0 16 16" width="10" height="10" aria-hidden="true" focusable="false"><path d="M10.5 2.5l3 3L6 13l-3.5.5L3 10z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>';
      pick.append((this.dataset.pickLabel || '').replace('{option}', pickOption.toLowerCase()));
      text.append(pick);
    }

    // Prices sit in their own right-aligned column (easy to scan down); the currency code is left to
    // the total on the button.
    const prices = document.createElement('span');
    prices.className = 'bl-tray__price';
    if (price) {
      const now = document.createElement('span');
      now.className = 'bl-tray__price-now';
      now.textContent = this.#shortMoney(price);
      prices.append(now);
      if (compareAt && compareAt !== price) {
        const was = document.createElement('s');
        was.textContent = this.#shortMoney(compareAt);
        prices.append(was);
      }
    }

    li.append(media, text, prices);

    // "Only the set from the link" (data-locked): the held charms can be edited but not removed.
    if (!current && key && !this.hasAttribute('data-locked')) {
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'bl-tray__remove';
      remove.setAttribute('aria-label', `${this.dataset.removeLabel || 'Remove'} ${title}`);
      remove.innerHTML =
        '<svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path d="M4 4l8 8M12 4l-8 8" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
      remove.addEventListener('click', () => {
        // The rows are re-rendered: put keyboard focus on the next remove button (or the previous one,
        // or the add-on tabs when the set is now empty) instead of losing it to the page.
        const buttons = Array.from(this.querySelectorAll('.bl-tray__remove'));
        const position = buttons.indexOf(remove);
        this.removeItem(key);
        const after = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.bl-tray__remove')));
        const next =
          after[Math.min(position, after.length - 1)] ||
          /** @type {HTMLElement | null} */ (
            this.closest('addon-categories-component')?.querySelector('.bl-addons__tab[aria-selected="true"], .bl-addons__tile')
          );
        next?.focus({ preventScroll: after.length > 0 });
      });
      li.append(remove);
    } else {
      // Keeps the price column aligned with the rows that have a remove button.
      const spacer = document.createElement('span');
      spacer.className = 'bl-tray__remove-spacer';
      li.append(spacer);
    }
    return li;
  }
}

if (!customElements.get('bl-addon-tray')) {
  customElements.define('bl-addon-tray', AddonTray);
}
